"use client"

import { useEffect } from "react"
import { DISCORD_INVITE_URL } from "@/lib/discord-invite"
import { SHOW_DISCORD } from "@/lib/discord-visibility"

// Self-contained on purpose (inline <style>, no Tailwind, no providers, no
// translations of its own): this is the screen people see when something has
// gone wrong, including when the root layout itself has crashed (global-error),
// so it must not depend on anything that might be the thing that broke.

export interface MaintenanceScreenProps {
  title: string
  body: string
  retryLabel: string
  homeLabel: string
  helpLabel: string
  referenceLabel: string
  /** Error digest from Next, shown small so support can find the server log. */
  digest?: string
  /** Called by "Try again". Defaults to a full page reload. */
  onRetry?: () => void
}

const CSS = `
.gm{--bg:#f4f4f5;--fg:#18181b;--muted:#52525b;--btn:#6d4aff;--btn-fg:#fff;--ghost-bd:#d4d4d8;--ghost-fg:#18181b;--link:#5b3fd6;--tape-a:#6d4aff;--tape-b:#ffffff;
  min-height:100vh;min-height:100dvh;display:flex;align-items:center;justify-content:center;padding:56px 16px;
  background:var(--bg);color:var(--fg);font-family:var(--font-sans),"Geist",ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;text-align:center;position:relative;overflow-x:clip}
.gm-dark{--tape-b:#0b0814;--bg:#0f0b1a;--fg:#f5f3ff;--muted:#b7b0d1;--btn:#8466ff;--btn-fg:#0f0b1a;--ghost-bd:#3a3158;--ghost-fg:#f5f3ff;--link:#a99bff}
.gm-inner{width:100%;max-width:520px;position:relative;z-index:1}
.gm-art-wrap{display:flex;justify-content:center;margin:0 0 22px}
.gm-art{flex:none;width:min(600px,160vw,66vh);max-width:none;height:auto;display:block;user-select:none;-webkit-user-drag:none}
.gm-art-dark{display:none}
.gm-dark .gm-art-light{display:none}
.gm-dark .gm-art-dark{display:block;filter:drop-shadow(0 18px 28px rgba(109,74,255,.28))}
.gm h1{font-size:clamp(1.6rem,5.2vw,2.1rem);line-height:1.15;font-weight:800;margin:0 0 16px;letter-spacing:-.01em}
.gm p{margin:0 auto;max-width:420px;font-size:1rem;line-height:1.55;color:var(--muted);text-wrap:balance}
.gm-actions{display:flex;flex-wrap:wrap;gap:12px;justify-content:center;margin-top:32px}
.gm-btn{appearance:none;border:1px solid transparent;border-radius:12px;padding:12px 22px;font-size:.95rem;font-weight:700;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;min-width:130px;transition:transform .12s ease,opacity .12s ease}
.gm-btn:hover{transform:translateY(-1px);opacity:.92}
.gm-btn:active{transform:translateY(0)}
.gm-btn:focus-visible,.gm a:focus-visible{outline:3px solid var(--link);outline-offset:2px}
.gm-primary{background:var(--btn);color:var(--btn-fg)}
.gm-ghost{background:transparent;color:var(--ghost-fg);border-color:var(--ghost-bd)}
.gm .gm-help{margin-top:34px;font-size:.85rem}
.gm-help a{color:var(--link);font-weight:600;text-decoration:underline;text-underline-offset:3px}
.gm .gm-ref{margin-top:14px;font-size:.72rem;color:var(--muted);opacity:.75;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;word-break:break-all}
@media (prefers-reduced-motion:no-preference){.gm-art{animation:gm-float 5s ease-in-out infinite}.gm-tape{animation:gm-tape 5s linear infinite}}
.gm-tape{position:fixed;left:0;right:0;height:40px;z-index:0;pointer-events:none;opacity:.3;background-image:linear-gradient(135deg,var(--tape-a) 25%,var(--tape-b) 25%,var(--tape-b) 50%,var(--tape-a) 50%,var(--tape-a) 75%,var(--tape-b) 75%);background-size:40px 40px}
.gm-tape-top{top:0;-webkit-mask-image:linear-gradient(to bottom,#000 30%,transparent);mask-image:linear-gradient(to bottom,#000 30%,transparent)}
.gm-tape-bottom{bottom:0;-webkit-mask-image:linear-gradient(to top,#000 30%,transparent);mask-image:linear-gradient(to top,#000 30%,transparent)}
@keyframes gm-tape{to{background-position:40px 0}}
@media (max-height:720px){.gm{padding:40px 16px}.gm-art-wrap{margin-bottom:10px}.gm h1{margin-bottom:10px}.gm-actions{margin-top:20px}.gm .gm-help{margin-top:20px}}
@keyframes gm-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@media (prefers-color-scheme:dark){.gm-auto{--tape-b:#0b0814;--bg:#0f0b1a;--fg:#f5f3ff;--muted:#b7b0d1;--btn:#8466ff;--btn-fg:#0f0b1a;--ghost-bd:#3a3158;--ghost-fg:#f5f3ff;--link:#a99bff}
  .gm-auto .gm-art-light{display:none}.gm-auto .gm-art-dark{display:block;filter:drop-shadow(0 18px 28px rgba(109,74,255,.28))}}
`

function readSavedTheme(): "dark" | "light" | "auto" | null {
  try {
    const raw = localStorage.getItem("booru-settings")
    const theme = raw ? JSON.parse(raw)?.state?.theme : null
    return theme === "dark" || theme === "light" || theme === "auto" ? theme : null
  } catch {
    return null
  }
}

export function MaintenanceScreen({
  title,
  body,
  retryLabel,
  homeLabel,
  helpLabel,
  referenceLabel,
  digest,
  onRetry,
}: MaintenanceScreenProps) {
  // Match the user's chosen site theme. Inside the normal layout the theme is a
  // class on <html>; if the whole layout is gone (global-error) fall back to the
  // saved setting, then to the device preference (the .gm-auto media query).
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(".gm")
    if (!root) return
    const html = document.documentElement.classList
    const saved = readSavedTheme()
    const mode = html.contains("dark") ? "dark" : html.contains("auto") ? "auto" : saved ?? "auto"
    root.classList.toggle("gm-dark", mode === "dark")
    root.classList.toggle("gm-auto", mode === "auto")
  }, [])

  return (
    <div className="gm gm-auto" role="alert">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="gm-tape gm-tape-top" aria-hidden="true" />
      <div className="gm-tape gm-tape-bottom" aria-hidden="true" />
      <div className="gm-inner">
        <div className="gm-art-wrap">
          {/* eslint-disable-next-line @next/next/no-img-element -- must work with no app providers; static art */}
          <img className="gm-art gm-art-light" src="/screens/maintenance/glym-maintenance.webp" alt="" width={1000} height={704} draggable={false} />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className="gm-art gm-art-dark" src="/screens/maintenance/glym-maintenance-dark.webp" alt="" width={1000} height={704} draggable={false} />
        </div>
        <h1>{title}</h1>
        <p>{body}</p>
        <div className="gm-actions">
          <button type="button" className="gm-btn gm-primary" onClick={onRetry ?? (() => window.location.reload())}>
            {retryLabel}
          </button>
          <a className="gm-btn gm-ghost" href="/">
            {homeLabel}
          </a>
        </div>
        <p className="gm-help">
          <a href={SHOW_DISCORD ? DISCORD_INVITE_URL : "mailto:support@goonru.app"} target="_blank" rel="noopener noreferrer">
            {helpLabel}
          </a>
        </p>
        {digest ? (
          <p className="gm-ref">
            {referenceLabel}: {digest}
          </p>
        ) : null}
      </div>
    </div>
  )
}
